import { MAPS, type MapDefinition } from "../game/Maps";

const MAP_TAGLINES: Record<string, string> = {
  foundry: "Industrial lanes",
  catacomb: "Split-level vaults",
  spire: "Vertical climb",
};

export const mapPickerTemplate = (selectedId = "foundry"): string => `
  <select id="match-map" class="map-picker-select" aria-hidden="true" tabindex="-1">
    ${MAPS.map((map) => `<option value="${map.id}"${map.id === selectedId ? " selected" : ""}>${map.name}</option>`).join("")}
  </select>
  <div id="map-picker" class="map-picker" role="listbox" aria-label="Choose map">
    ${MAPS.map((map) => mapCard(map, map.id === selectedId)).join("")}
  </div>`;

export const bindMapPicker = (onSelect: (mapId: string) => void): void => {
  const picker = document.getElementById("map-picker");
  picker?.addEventListener("click", (event) => {
    if (!(event.target instanceof Element)) return;
    const card = event.target.closest<HTMLButtonElement>("[data-map]");
    if (!card || card.disabled) return;
    const mapId = card.dataset.map;
    if (!mapId) return;
    onSelect(mapId);
  });
  picker?.addEventListener("keydown", (event) => {
    if (!(event.target instanceof HTMLButtonElement)) return;
    const cards = mapCards().filter((card) => !card.disabled);
    const currentIndex = cards.indexOf(event.target);
    const nextIndex = nextCardIndex(event.key, currentIndex, cards.length);
    if (nextIndex === null) return;
    event.preventDefault();
    const card = cards[nextIndex];
    const mapId = card.dataset.map;
    if (!mapId) return;
    card.focus();
    onSelect(mapId);
  });
};

export const syncMapPicker = (): void => {
  const selectedId = (document.getElementById("match-map") as HTMLSelectElement | null)?.value;
  for (const card of mapCards()) {
    const selected = card.dataset.map === selectedId;
    card.classList.toggle("selected", selected);
    card.setAttribute("aria-selected", String(selected));
    card.tabIndex = selected ? 0 : -1;
  }
};

export const setMapPickerDisabled = (disabled: boolean): void => {
  for (const card of mapCards()) card.disabled = disabled;
};

const mapCards = (): HTMLButtonElement[] => [...document.querySelectorAll<HTMLButtonElement>("#map-picker [data-map]")];

const nextCardIndex = (key: string, currentIndex: number, cardCount: number): number | null => {
  if (cardCount === 0) return null;
  if (key === "Home") return 0;
  if (key === "End") return cardCount - 1;
  if (key === "ArrowRight" || key === "ArrowDown") return (currentIndex + 1 + cardCount) % cardCount;
  if (key === "ArrowLeft" || key === "ArrowUp") return (currentIndex - 1 + cardCount) % cardCount;
  return null;
};

const mapCard = (map: MapDefinition, selected: boolean): string => `
  <button class="map-card${selected ? " selected" : ""}" type="button" data-map="${map.id}" role="option" aria-selected="${selected}">
    <svg class="map-card-preview" viewBox="0 0 ${map.width} ${map.height}" aria-hidden="true" focusable="false">
      <rect width="${map.width}" height="${map.height}" fill="${colorHex(map.backdrop)}" />
      <path d="M0 150H${map.width}M0 450H${map.width}M0 750H${map.width}" stroke="${colorHex(map.accent)}" stroke-opacity=".12" />
      ${map.solids.map((solid) => `<rect x="${solid.x}" y="${solid.y}" width="${solid.width}" height="${solid.height}" rx="3" fill="#171923" stroke="${colorHex(map.accent)}" stroke-opacity=".78" stroke-width="4" />`).join("")}
      ${map.crates.map((crate) => `<rect x="${crate.x}" y="${crate.y}" width="${crate.width}" height="${crate.height}" fill="#9a5141" fill-opacity=".78" stroke="#d77b55" stroke-opacity=".8" stroke-width="3" />`).join("")}
      ${map.spawns.map((spawn) => `<circle cx="${spawn.x}" cy="${spawn.y}" r="10" fill="${colorHex(map.accent)}" fill-opacity=".9" />`).join("")}
    </svg>
    <span class="map-card-name">${map.name}</span>
    <span class="map-card-tagline">${MAP_TAGLINES[map.id] ?? "Arena layout"}</span>
  </button>`;

const colorHex = (value: number): string => `#${value.toString(16).padStart(6, "0")}`;
